import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReportMetadata } from '../../types';
import { heuteLokalIso } from '../../components/planungsphase/planungLogic';
import {
    fetchEntscheidungen,
    fetchLetzteAusgaben,
    speichereEntscheidung,
    type AusgabeUnterlage,
    type EntscheidungErgebnis,
    type Fassungsentscheidung,
    type FassungsEntscheid,
    type UnterlagenAusgabe,
} from '../unterlagenAusgabenService';
import { abweichungsStaende, type AbweichungsStand } from './abweichungen';
import { ladeSteckbriefQuellen, lesefehlerText } from './quellen';
import { baueSteckbrief, type SteckbriefQuellen } from './steckbrief';

/**
 * Der Aenderungsdienst eines Projekts fuer Dialog, Modulhinweis und Reiter
 * „Übergabe" (Plan Stufe 6.1): je Unterlage mit geltender Fassung die
 * Abweichungen zwischen eingefrorenem und heutigem Steckbrief, die
 * Entscheidung dazu und ob die Frage offen ist.
 *
 * ═══ LESEN JA, SCHREIBEN NUR PER `entscheide` ═════════════════════════════
 * Der Hook laedt und rechnet. Geschrieben wird allein ueber `entscheide` —
 * eine Zeile in `unterlagen_fassungsentscheide`, auf Klick. Nichts wird beim
 * Laden angelegt (dasselbe Versprechen wie `useProjektSteckbrief`).
 *
 * ═══ `bereit` IST TEIL DER AUSSAGE ════════════════════════════════════════
 * Vor der Antwort ist `staende` leer — und leer heisst „noch nicht gelesen",
 * nicht „nichts veraltet". Ebenso `lesefehler`: konnten Steckbrief, Ausgaben
 * oder Entscheidungen nicht gelesen werden, bleibt `staende` LEER und
 * `lesefehler` sagt warum; eine Ampel muss den Fehler zeigen statt gruen zu
 * werden.
 *
 * ═══ UNVOLLSTAENDIGE QUELLEN ERGEBEN KEINE ENTSCHEIDBAREN STAENDE ═════════
 * Aus Ersatzwerten gerechnete Abweichungen sind keine: ein unlesbarer
 * Steckbrief meldete jedes Merkmal als „weg", unlesbare Entscheidungen
 * liessen laengst bewertete Fragen wieder offen erscheinen — und ein Klick
 * darauf schriebe einen Entscheid zu einer Abweichung, die es nie gab
 * (Codex-Review 17.10.2026). Deshalb: bei `lesefehler` keine Staende, und
 * `entscheide` ist gesperrt, bis ein Lesen vollstaendig gelang
 * (`entscheidbar`). Der Zustand heisst „konnte nicht geprueft werden" —
 * weder Entwarnung noch Abweichung.
 *
 * ═══ PROJEKTDATEN BEIM RECHNEN, NICHT BEIM LADEN ══════════════════════════
 * `metadata` ist bei jedem Rendern ein frisches Objekt; der Ladeeffekt
 * haengt nur an `projectId`, `baueSteckbrief` mischt die aktuellen
 * Projektdaten im `useMemo` dazu (Muster `useProjektSteckbrief`). Eine
 * Aenderung an Bauherr oder Bauzeit zeigt sich damit sofort als Abweichung.
 */

/** Alles, was ein Durchlauf gelesen hat — ohne React, damit es testbar ist. */
export interface AbweichungsQuellen {
    quellen: SteckbriefQuellen;
    ausgaben: Partial<Record<AusgabeUnterlage, UnterlagenAusgabe>>;
    entscheidungen: Fassungsentscheidung[];
    /** Alle Lesefehler als ein Satz, `null` wenn alles gelesen wurde. */
    lesefehler: string | null;
}

/**
 * Liest Steckbrief-Quellen, letzte Ausgaben und Entscheidungen parallel.
 * Lehnt nie ab — jeder Leser ist fuer sich fail-open, und was nicht gelesen
 * werden konnte, steht in `lesefehler`.
 */
export const ladeAbweichungsQuellen = async (
    projectId: string,
    metadata: Partial<ReportMetadata> | null,
): Promise<AbweichungsQuellen> => {
    const [quellen, letzte, entscheide] = await Promise.all([
        ladeSteckbriefQuellen(projectId, metadata),
        fetchLetzteAusgaben(projectId),
        fetchEntscheidungen(projectId),
    ]);
    const teile: string[] = [];
    const steckbrief = lesefehlerText(quellen.lesefehler);
    if (steckbrief) teile.push(steckbrief);
    if (letzte.lesefehler) teile.push(`Ausgaben: ${letzte.lesefehler}`);
    if (entscheide.lesefehler) teile.push(`Entscheidungen: ${entscheide.lesefehler}`);
    return {
        quellen,
        ausgaben: letzte.ausgaben,
        entscheidungen: entscheide.entscheidungen,
        lesefehler: teile.length ? teile.join(' · ') : null,
    };
};

/**
 * Die Staende eines Durchlaufs — NUR, wenn alle Quellen gelesen wurden.
 * Bei `lesefehler` leer: aus Ersatzwerten entstuenden Abweichungen, die es
 * nicht gibt, und entschiedene Fragen erschienen wieder offen (Dateikopf).
 * Pur, damit Hook und Rueckfrage der Planungsphase dieselbe Regel haben.
 */
export const staendeAusQuellen = (
    q: AbweichungsQuellen,
    metadata: Partial<ReportMetadata> | null,
    standTag: string,
): AbweichungsStand[] => {
    if (q.lesefehler) return [];
    return abweichungsStaende(q.ausgaben, q.entscheidungen, baueSteckbrief({ ...q.quellen, metadata }, standTag));
};

/** Der Satz, mit dem ein Entscheid abgewiesen wird, solange nicht vollstaendig gelesen ist. */
export const ENTSCHEID_GESPERRT =
    'Der Abgleich mit der Planungsphase konnte nicht vollständig gelesen werden — ein Entscheid ist erst nach erfolgreichem Lesen möglich.';

interface Gelesen extends AbweichungsQuellen {
    fuer: string;
    standTag: string;
    /** Zu welchem Leseauftrag (`lauf`, `neuladen`) diese Daten gehoeren. */
    auftrag: string;
}

export interface AbweichungenStand {
    /** Je Unterlage mit letzter Ausgabe; leer, solange nicht `bereit` — und leer bei `lesefehler`. */
    staende: AbweichungsStand[];
    /** Erst `true`, wenn die Daten zu GENAU diesem Projekt vorliegen. */
    bereit: boolean;
    /** Lesefehler als Satz — dann ist nichts geprueft, `staende` ist leer, `entscheide` gesperrt. */
    lesefehler: string | null;
    /** `bereit` und ohne Lesefehler: nur dann darf ein Entscheid geschrieben werden. */
    entscheidbar: boolean;
    /** Alles neu lesen, etwa nach einer Freigabe oder nach Aenderungen in der Planungsphase. */
    neuLaden: () => void;
    /**
     * Eine der drei Antworten festhalten; bei Erfolg wird neu gelesen. Solange
     * nicht `entscheidbar`, kommt `status: 'fehler'` mit `ENTSCHEID_GESPERRT`
     * zurueck, ohne dass etwas geschrieben wird.
     */
    entscheide: (stand: AbweichungsStand, entscheid: FassungsEntscheid, begruendung: string | null) => Promise<EntscheidungErgebnis>;
}

/**
 * @param neuladen Auslöser von aussen: jede Aenderung des Werts liest neu
 *   (Muster: ein Zaehler im Modul, hochgezaehlt nach Freigabe-Journal,
 *   „Erneut festhalten" und „Neue Revision"). Ohne ihn zeigte der Hinweis
 *   nach einer Freigabe weiter die Abweichung der ALTEN Fassung, und ein
 *   Entscheid darauf eroeffnete die frisch freigegebene Fassung gleich wieder
 *   (Codex-Review 17.10.2026).
 */
export const useAbweichungen = (
    projectId: string,
    metadata: Partial<ReportMetadata> | null,
    neuladen: number = 0,
): AbweichungenStand => {
    const [gelesen, setGelesen] = useState<Gelesen | null>(null);
    const [lauf, setLauf] = useState(0);

    const neuLaden = useCallback(() => setLauf(n => n + 1), []);

    const auftrag = `${lauf}:${neuladen}`;

    useEffect(() => {
        let gilt = true;
        // Die Projektdaten kommen unten im `useMemo` dazu — hier `null`.
        void ladeAbweichungsQuellen(projectId, null).then(q => {
            if (gilt) setGelesen({ ...q, fuer: projectId, standTag: heuteLokalIso(), auftrag });
        });
        return () => { gilt = false; };
    }, [projectId, auftrag]);

    const bereit = gelesen !== null && gelesen.fuer === projectId;
    // Waehrend neu gelesen wird, bleibt der alte Stand sichtbar, aber nicht
    // entscheidbar: ein Klick darauf traefe sonst die Ausgabe von gestern.
    const entscheidbar = bereit && gelesen !== null && gelesen.auftrag === auftrag && !gelesen.lesefehler;

    const staende = useMemo(
        () => (bereit && gelesen ? staendeAusQuellen(gelesen, metadata, gelesen.standTag) : []),
        [bereit, gelesen, metadata],
    );

    const entscheide = useCallback(async (
        stand: AbweichungsStand,
        entscheid: FassungsEntscheid,
        begruendung: string | null,
    ): Promise<EntscheidungErgebnis> => {
        // Schreibweg gesperrt, bis vollstaendig gelesen ist: ein Stand, der
        // aus einem Lesefehler stammt, darf keinen Entscheid bekommen.
        if (!entscheidbar) return { status: 'fehler', fehler: ENTSCHEID_GESPERRT };
        const ergebnis = await speichereEntscheidung({
            projectId,
            unterlage: stand.unterlage,
            ausgabeId: stand.ausgabe.id,
            abweichungHash: stand.hash,
            abweichungen: stand.abweichungen,
            entscheid,
            begruendung,
        });
        if (ergebnis.status === 'ok') neuLaden();
        return ergebnis;
    }, [projectId, neuLaden, entscheidbar]);

    return {
        staende,
        bereit,
        lesefehler: bereit && gelesen ? gelesen.lesefehler : null,
        entscheidbar,
        neuLaden,
        entscheide,
    };
};
